// 🔐 Connexion P5 — Historique de sécurité (propriétaire) : filtres, liste chronologique, « Charger plus », export CSV.
import { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import * as Icones from 'lucide-react';
import { tempsRelatif } from '@/components/securite/Appareils';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const headers = () => ({ Authorization: `Bearer ${localStorage.getItem('auth_token')}` });
const GRAVITE = { info: 'bg-slate-100 text-slate-700 border-slate-200', attention: 'bg-amber-50 text-amber-800 border-amber-200', critique: 'bg-red-50 text-red-800 border-red-200' };
const PERIODES = [['24h', '24 h', 1], ['7j', '7 j', 7], ['30j', '30 j', 30], ['perso', 'Personnalisée', null]];
const TAILLE = 50;

export function HistoriqueSecurite({ utilisateurInitial = null, typesInitiaux = [] }) {
  const [types, setTypes] = useState({});
  const [selection, setSelection] = useState(typesInitiaux); // [] = tous ; 🔔 Lot 2 : pré-filtre depuis l'URL (?types=a,b)
  const [q, setQ] = useState('');
  const [periode, setPeriode] = useState('7j');
  const [perso, setPerso] = useState({ depuis: '', jusqua: '' });
  const [inclureReussies, setInclureReussies] = useState(false);
  const [utilisateurId, setUtilisateurId] = useState(utilisateurInitial);
  const [data, setData] = useState({ total: 0, items: [] });
  const [page, setPage] = useState(1);
  const [chargement, setChargement] = useState(false);

  useEffect(() => { axios.get(`${API_URL}/api/security/historique/types`, { headers: headers() }).then((r) => setTypes(r.data.types)).catch(() => {}); }, []);
  useEffect(() => { setUtilisateurId(utilisateurInitial); }, [utilisateurInitial]);

  const params = useMemo(() => {
    const p = { taille: TAILLE, inclure_connexions_reussies: inclureReussies };
    if (selection.length) p.types = selection.join(',');
    if (utilisateurId) p.utilisateur_id = utilisateurId; else if (q.trim()) p.q = q.trim();
    const jours = PERIODES.find(([k]) => k === periode)?.[2];
    if (jours) p.depuis = new Date(Date.now() - jours * 86400000).toISOString();
    else { if (perso.depuis) p.depuis = new Date(perso.depuis).toISOString(); if (perso.jusqua) p.jusqua = new Date(`${perso.jusqua}T23:59:59`).toISOString(); }
    return p;
  }, [selection, q, periode, perso, inclureReussies, utilisateurId]);

  const charger = useCallback(async (pageDemandee) => {
    setChargement(true);
    try {
      const { data: r } = await axios.get(`${API_URL}/api/security/historique`, { params: { ...params, page: pageDemandee }, headers: headers() });
      setData((d) => ({ total: r.total, items: pageDemandee === 1 ? r.items : [...d.items, ...r.items] }));
      setPage(pageDemandee);
    } catch (e) { toast.error(e.response?.data?.detail || "Impossible de charger l'historique"); }
    finally { setChargement(false); }
  }, [params]);
  useEffect(() => { charger(1); }, [charger]);

  const exporter = async () => {
    try {
      const r = await axios.get(`${API_URL}/api/security/historique/export.csv`, { params, headers: headers(), responseType: 'blob' });
      const url = URL.createObjectURL(r.data); const a = document.createElement('a'); a.href = url; a.download = `historique-securite-${new Date().toISOString().slice(0, 10)}.csv`; a.click(); URL.revokeObjectURL(url);
    } catch { toast.error("Échec de l'export CSV"); }
  };

  const groupes = useMemo(() => Object.entries(types).reduce((acc, [cle, t]) => { (acc[t.groupe] ||= []).push([cle, t]); return acc; }, {}), [types]);
  const toggleType = (cle) => setSelection((s) => (s.includes(cle) ? s.filter((x) => x !== cle) : [...s, cle]));

  return (
    <Card data-testid="section-historique">
      <CardHeader>
        <CardTitle>Historique de sécurité</CardTitle>
        <CardDescription>Événements de sécurité et échecs de connexion (connexions réussies via le filtre). Lecture seule, toutes sources confondues.</CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="space-y-2" data-testid="historique-filtres">
          <div className="flex flex-wrap gap-2 items-center">
            {PERIODES.map(([k, l]) => <Button key={k} size="sm" variant={periode === k ? 'default' : 'outline'} onClick={() => setPeriode(k)} data-testid={`historique-periode-${k}`}>{l}</Button>)}
            {periode === 'perso' && <><Input type="date" className="h-8 w-40" value={perso.depuis} onChange={(e) => setPerso({ ...perso, depuis: e.target.value })} data-testid="historique-depuis" /><Input type="date" className="h-8 w-40" value={perso.jusqua} onChange={(e) => setPerso({ ...perso, jusqua: e.target.value })} data-testid="historique-jusqua" /></>}
            <label className="flex items-center gap-1.5 text-sm ml-2"><input type="checkbox" checked={inclureReussies} onChange={(e) => setInclureReussies(e.target.checked)} data-testid="historique-inclure-reussies" /> Inclure les connexions réussies</label>
          </div>
          <div className="flex flex-wrap gap-2 items-center">
            {utilisateurId ? (
              <Badge variant="outline" className="gap-1" data-testid="historique-filtre-personne">Personne : {data.items.find((i) => i.cible?.id === utilisateurId || i.acteur?.id === utilisateurId)?.cible?.nom || utilisateurId}<button type="button" onClick={() => setUtilisateurId(null)} aria-label="Retirer le filtre personne" data-testid="historique-filtre-personne-retirer">×</button></Badge>
            ) : <Input placeholder="Personne (nom ou courriel)…" className="h-8 w-64" value={q} onChange={(e) => setQ(e.target.value)} data-testid="historique-recherche" />}
            {selection.length > 0 && <Button size="sm" variant="ghost" onClick={() => setSelection([])} data-testid="historique-types-tous">Tous les types</Button>}
          </div>
          <div className="flex flex-wrap gap-x-4 gap-y-1" data-testid="historique-types">
            {Object.entries(groupes).map(([g, liste]) => (
              <div key={g} className="flex flex-wrap items-center gap-1">
                <span className="text-[11px] uppercase tracking-wide text-slate-400 mr-1">{g}</span>
                {liste.map(([cle, t]) => <button type="button" key={cle} onClick={() => toggleType(cle)} className={`text-xs px-2 py-0.5 rounded-full border ${selection.includes(cle) ? 'bg-slate-800 text-white border-slate-800' : 'bg-white text-slate-600'}`} data-testid={`historique-type-${cle}`}>{t.libelle}</button>)}
              </div>
            ))}
          </div>
        </div>

        <div className="flex items-center justify-between text-sm text-slate-500">
          <span data-testid="historique-total">{data.total} événement{data.total > 1 ? 's' : ''}</span>
          <Button size="sm" variant="outline" onClick={exporter} data-testid="historique-exporter"><Icones.Download className="w-3.5 h-3.5 mr-1" /> Exporter CSV</Button>
        </div>

        <ol className="space-y-1.5" data-testid="historique-liste">
          {data.items.length === 0 && !chargement && <li className="text-sm text-slate-400 py-4 text-center" data-testid="historique-vide">Aucun événement pour ces filtres</li>}
          {data.items.map((it) => {
            const Icone = Icones[it.icone] || Icones.Shield;
            return (
              <li key={it.id} className={`flex items-start gap-3 border rounded-lg px-3 py-2 ${GRAVITE[it.gravite]}`} data-testid={`historique-item-${it.id}`}>
                <Icone className="w-4 h-4 mt-0.5 shrink-0" />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium">{it.libelle}
                    {(it.acteur || it.cible) && <span className="font-normal text-slate-600"> — {it.acteur?.nom || it.acteur?.email || ''}{it.acteur && it.cible && it.cible.id !== it.acteur.id ? ' → ' : ''}{it.cible && it.cible.id !== it.acteur?.id ? (it.cible.nom || it.cible.email || it.cible.id) : ''}</span>}
                  </p>
                  <div className="flex flex-wrap gap-1 mt-1">
                    {Object.entries(it.details || {}).slice(0, 6).map(([k, v]) => <span key={k} className="text-[11px] bg-white/70 border rounded px-1.5 py-0.5" data-testid="historique-detail">{k} : {typeof v === 'object' ? JSON.stringify(v) : String(v)}</span>)}
                    {it.ip && <span className="text-[11px] text-slate-500">IP {it.ip}</span>}
                  </div>
                </div>
                <time className="text-xs text-slate-500 whitespace-nowrap" title={it.date ? new Date(it.date).toLocaleString('fr-CA') : ''} data-testid="historique-date">{tempsRelatif(it.date)}</time>
              </li>
            );
          })}
        </ol>
        {data.items.length < data.total && <div className="text-center"><Button size="sm" variant="outline" disabled={chargement} onClick={() => charger(page + 1)} data-testid="historique-charger-plus">{chargement ? 'Chargement…' : 'Charger plus'}</Button></div>}
      </CardContent>
    </Card>
  );
}
